import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Wand2, Mic, Music, Play, Pause, Trash2, Download, Check, Upload, Radio, Save, Clock, CalendarDays, Star } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const inputCls = "mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]";
const selectCls = "mt-1.5 w-full rounded-md bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]";
const card = "rounded-xl border border-white/10 bg-[#0C1338] p-4";
const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const schedLabel = (j) => {
  const parts = [];
  if (j.days?.length && j.days.length < 7) parts.push(j.days.slice().sort().map((d) => DAYS[d][0]).join(" "));
  if (j.start_time && j.end_time) parts.push(`${j.start_time}–${j.end_time}`);
  else if (j.start_time) parts.push(`from ${j.start_time}`);
  else if (j.end_time) parts.push(`until ${j.end_time}`);
  return parts.length ? parts.join(" · ") : "Any time";
};
const Sparkline = ({ daily = [], week = 0, testid }) => {
  const max = Math.max(...daily, 1);
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="flex items-end gap-0.5 h-6" title={`${week} plays this week`}>
        {daily.map((v, i) => (
          <div key={i} className="w-1.5 rounded-sm bg-[#E6B34A]" style={{ height: `${Math.max(2, (v / max) * 24)}px`, opacity: v ? 1 : 0.25 }} />
        ))}
      </div>
      <span className="text-[11px] font-bold text-zinc-300 tabular-nums">{week} <span className="font-normal text-zinc-500">plays/wk</span></span>
    </div>
  );
};

export default function JingleStudio() {
  const token = localStorage.getItem("token");
  const [opts, setOpts] = useState({ voices: [], bed_styles: [], lengths: [5, 10, 15, 30], stations: [], programs: [] });
  const [mode, setMode] = useState("prompt"); // "prompt" | "auto"
  const [prompt, setPrompt] = useState("");
  const [length, setLength] = useState(10);
  const [voice, setVoice] = useState("auto");
  const [bedStyle, setBedStyle] = useState("pad");
  const [bedVolume, setBedVolume] = useState(0.22);
  const [bedAudio, setBedAudio] = useState(null);
  const [variants, setVariants] = useState([]);
  const [script, setScript] = useState("");
  const [audio, setAudio] = useState(null); // { path, ext, url }
  const [title, setTitle] = useState("");
  const [inRotation, setInRotation] = useState(true);
  const [stationIds, setStationIds] = useState([]);
  const [programIds, setProgramIds] = useState([]);
  const [busy, setBusy] = useState(null);
  const [jingles, setJingles] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [mix, setMix] = useState(30);
  const [days, setDays] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [schedEdit, setSchedEdit] = useState(null); // { id, days, start_time, end_time }
  const [analytics, setAnalytics] = useState({});
  const previewRef = useRef(null);
  const bedRef = useRef(null);

  const loadJingles = () => api.get("/jingles").then((r) => setJingles(r.data || [])).catch(() => {});
  const loadAnalytics = () => api.get("/jingles/analytics").then((r) => setAnalytics(r.data?.jingles || {})).catch(() => {});
  useEffect(() => {
    api.get("/jingles/options").then((r) => setOpts(r.data)).catch(() => {});
    api.get("/jingles/mix").then((r) => setMix(r.data?.jingle_mix ?? 30)).catch(() => {});
    loadJingles();
    loadAnalytics();
    return () => { try { previewRef.current?.pause(); } catch (e) {} };
  }, []);

  const saveMix = async (v) => { setMix(v); try { await api.post("/jingles/mix", { jingle_mix: v }); } catch (e) {} };
  const toggleDay = (d) => setDays((xs) => xs.includes(d) ? xs.filter((x) => x !== d) : [...xs, d]);

  const toggleIn = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const writeScript = async () => {
    if (mode === "prompt" && !prompt.trim()) { toast.error("Type a short idea for the jingle first"); return; }
    setBusy("script");
    try {
      const { data } = await api.post("/jingles/script", { prompt, auto: mode === "auto", length, variations: 3 });
      setVariants(data.variations || []);
      if (data.variations?.length) setScript(data.variations[0]);
      toast.success("Jingle options ready — pick one");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not write the jingle"); }
    finally { setBusy(null); }
  };

  const uploadBed = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy("bed");
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("save_to_library", "false"); fd.append("name", f.name);
      const r = await api.post("/ai-ads/bed-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBedAudio({ path: r.data.path, filename: r.data.filename });
      setBedStyle("pad");
      toast.success("Custom bed uploaded — it'll play under the jingle");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not upload music"); }
    finally { setBusy(null); }
  };

  const genVoiceover = async () => {
    if (!script.trim()) { toast.error("Write or pick a jingle line first"); return; }
    setBusy("voice");
    try {
      const { data } = await api.post("/jingles/voiceover", {
        script, voice, bed_style: bedStyle, bed_volume: bedVolume,
        bed_audio_path: bedAudio?.path || "",
      });
      setAudio({ path: data.path, ext: data.ext, url: `${API}/jingles/media?path=${encodeURIComponent(data.path)}&auth=${token}` });
      toast.success(`Voiced by ${data.voice_label} over ${data.bed_style_label}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate the voiceover"); }
    finally { setBusy(null); }
  };

  const playUrl = (id, url) => {
    try { previewRef.current?.pause(); } catch (e) {}
    if (playingId === id) { setPlayingId(null); return; }
    const a = new Audio(url);
    previewRef.current = a;
    setPlayingId(id);
    a.onended = () => setPlayingId(null);
    a.onerror = () => { setPlayingId(null); toast.error("Could not play that clip"); };
    a.play().catch(() => setPlayingId(null));
  };

  const saveJingle = async () => {
    if (!title.trim()) { toast.error("Give your jingle a title"); return; }
    if (!audio?.path) { toast.error("Generate the jingle audio first"); return; }
    setBusy("save");
    try {
      await api.post("/jingles", {
        title, prompt, script, voice, bed_style: bedStyle, length_sec: length,
        audio_path: audio.path, ext: audio.ext, in_rotation: inRotation,
        station_ids: stationIds, program_ids: programIds,
        days, start_time: startTime, end_time: endTime,
      });
      toast.success("Jingle saved");
      setTitle(""); setScript(""); setVariants([]); setAudio(null); setStationIds([]); setProgramIds([]);
      setDays([]); setStartTime(""); setEndTime("");
      loadJingles();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the jingle"); }
    finally { setBusy(null); }
  };

  const toggleRotation = async (j) => {
    try {
      const { data } = await api.patch(`/jingles/${j.id}`, { in_rotation: !j.in_rotation });
      setJingles((xs) => xs.map((x) => (x.id === j.id ? data : x)));
      toast.success(data.in_rotation ? "Added to station rotation" : "Removed from rotation");
    } catch (e) { toast.error("Could not update"); }
  };

  const removeJingle = async (id) => {
    if (!window.confirm("Delete this jingle?")) return;
    try { await api.delete(`/jingles/${id}`); setJingles((xs) => xs.filter((x) => x.id !== id)); toast.success("Jingle deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const toggleFav = async (j) => {
    try { const { data } = await api.post(`/jingles/${j.id}/favorite`); setJingles((xs) => xs.map((x) => (x.id === data.id ? data : x))); }
    catch (e) { toast.error("Could not update"); }
  };

  const openSched = (j) => setSchedEdit({ id: j.id, days: [...(j.days || [])], start_time: j.start_time || "", end_time: j.end_time || "" });
  const toggleSchedDay = (d) => setSchedEdit((s) => ({ ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d] }));
  const saveSched = async () => {
    try {
      const { data } = await api.patch(`/jingles/${schedEdit.id}`, { days: schedEdit.days, start_time: schedEdit.start_time, end_time: schedEdit.end_time });
      setJingles((xs) => xs.map((x) => (x.id === data.id ? data : x)));
      setSchedEdit(null);
      toast.success("Schedule updated");
    } catch (e) { toast.error("Could not save schedule"); }
  };

  return (
    <div className="space-y-6" data-testid="jingle-studio">
      <div className={card + " flex items-center gap-4 flex-wrap"} data-testid="jingle-mix-bar">
        <div className="min-w-0">
          <p className="text-sm font-bold text-white flex items-center gap-2"><Radio className="w-4 h-4 text-[#E6B34A]" /> Rotation mix</p>
          <p className="text-[11px] text-zinc-400">How often a jingle plays vs a paid ad in the between-song break.</p>
        </div>
        <div className="flex items-center gap-3 flex-1 min-w-[220px]">
          <span className="text-[11px] text-zinc-400 shrink-0">Ads</span>
          <input type="range" min="0" max="100" step="5" value={mix} onChange={(e) => saveMix(parseInt(e.target.value))} className="flex-1 accent-[#E6B34A]" data-testid="jingle-mix-slider" />
          <span className="text-[11px] text-zinc-400 shrink-0">Jingles</span>
          <span className="text-sm font-bold text-[#E6B34A] w-12 text-right tabular-nums" data-testid="jingle-mix-value">{mix}%</span>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
      {/* Create panel */}
      <div className={card}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-[#E6B34A]" />
          <h3 className="text-lg font-bold text-white">Create an AI jingle</h3>
        </div>

        <div className="flex gap-2 mb-4">
          <button data-testid="jingle-mode-prompt" onClick={() => setMode("prompt")}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${mode === "prompt" ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300"}`}>From my idea</button>
          <button data-testid="jingle-mode-auto" onClick={() => setMode("auto")}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${mode === "auto" ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300"}`}>Auto-suggest for me</button>
        </div>

        {mode === "prompt" && (
          <div className="mb-3">
            <label className="text-xs font-bold text-zinc-400">Your idea</label>
            <Input data-testid="jingle-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. energetic Sunday morning station ID" className={inputCls} />
          </div>
        )}

        <div className="mb-3">
          <label className="text-xs font-bold text-zinc-400">Length</label>
          <div className="mt-1.5 flex gap-2">
            {opts.lengths.map((l) => (
              <button key={l} data-testid={`jingle-length-${l}`} onClick={() => setLength(l)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${length === l ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300"}`}>{l}s</button>
            ))}
          </div>
        </div>

        <button data-testid="jingle-write-btn" onClick={writeScript} disabled={busy === "script"}
          className="w-full mb-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E2A5A] text-white text-sm font-bold hover:bg-[#263569] transition-colors disabled:opacity-60">
          {busy === "script" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Write jingle lines
        </button>

        {variants.length > 0 && (
          <div className="mb-4 space-y-2">
            <label className="text-xs font-bold text-zinc-400">Pick a line</label>
            {variants.map((v, i) => (
              <button key={i} data-testid={`jingle-variant-${i}`} onClick={() => setScript(v)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors border ${script === v ? "bg-[#E6B34A]/15 border-[#E6B34A] text-white" : "bg-[#1E2A5A] border-white/10 text-zinc-300 hover:border-white/30"}`}>
                {script === v && <Check className="w-3.5 h-3.5 inline mr-1.5 text-[#E6B34A]" />}{v}
              </button>
            ))}
          </div>
        )}

        {variants.length > 0 && (
          <div className="mb-3">
            <label className="text-xs font-bold text-zinc-400">Jingle text (editable)</label>
            <textarea data-testid="jingle-script" value={script} onChange={(e) => setScript(e.target.value)} rows={2}
              className={selectCls + " resize-none"} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-bold text-zinc-400">Voice</label>
            <select data-testid="jingle-voice" value={voice} onChange={(e) => setVoice(e.target.value)} className={selectCls}>
              {opts.voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-400">Music bed</label>
            <select data-testid="jingle-bed" value={bedStyle} onChange={(e) => { setBedStyle(e.target.value); setBedAudio(null); }} className={selectCls}>
              {opts.bed_styles.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select>
          </div>
        </div>

        {bedStyle !== "none" && (
          <div className="mb-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-zinc-400">Bed volume · {Math.round(bedVolume * 100)}%</label>
              <label data-testid="jingle-bed-upload" className="text-[11px] font-bold text-[#E6B34A] cursor-pointer inline-flex items-center gap-1">
                <Upload className="w-3 h-3" /> {bedAudio ? "Change track" : "Upload my track"}
                <input ref={bedRef} type="file" accept="audio/*" className="hidden" onChange={uploadBed} />
              </label>
            </div>
            <input type="range" min="0.02" max="0.5" step="0.01" value={bedVolume} onChange={(e) => setBedVolume(parseFloat(e.target.value))}
              className="w-full mt-1.5 accent-[#E6B34A]" data-testid="jingle-bed-volume" />
            {bedAudio && <p className="text-[11px] text-zinc-400 mt-1 truncate flex items-center gap-1"><Music className="w-3 h-3" /> {bedAudio.filename}</p>}
          </div>
        )}

        <button data-testid="jingle-voiceover-btn" onClick={genVoiceover} disabled={busy === "voice" || !script.trim()}
          className="w-full mb-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
          {busy === "voice" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />} Generate voiceover
        </button>

        {audio && (
          <div className="mb-4 rounded-lg border border-[#E6B34A]/30 bg-[#1E2A5A] p-3">
            <div className="flex items-center gap-2">
              <button data-testid="jingle-preview-new" onClick={() => playUrl("new", audio.url)} className="w-9 h-9 rounded-full bg-[#E6B34A] text-[#0A1030] flex items-center justify-center shrink-0">
                {playingId === "new" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">Your jingle preview</p>
                <p className="text-[11px] text-zinc-400 truncate">{script}</p>
              </div>
            </div>

            <div className="mt-3 border-t border-white/10 pt-3 space-y-3">
              <div>
                <label className="text-xs font-bold text-zinc-400">Title</label>
                <Input data-testid="jingle-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sunday Morning ID" className={inputCls} />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
                <input data-testid="jingle-in-rotation" type="checkbox" checked={inRotation} onChange={(e) => setInRotation(e.target.checked)} className="accent-[#E6B34A] w-4 h-4" />
                <Radio className="w-4 h-4 text-[#E6B34A]" /> Auto-play between songs on stations
              </label>
              {inRotation && (
                <div>
                  <label className="text-[11px] font-bold text-zinc-400">Stations {stationIds.length === 0 ? "(all)" : `(${stationIds.length})`}</label>
                  <div className="mt-1.5 max-h-28 overflow-y-auto rounded-lg border border-white/10 bg-[#0C1338] p-2 space-y-1">
                    {opts.stations.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                        <input type="checkbox" checked={stationIds.includes(s.id)} onChange={() => toggleIn(stationIds, setStationIds, s.id)} className="accent-[#E6B34A]" data-testid={`jingle-station-${s.id}`} />
                        {s.name}
                      </label>
                    ))}
                    {opts.stations.length === 0 && <p className="text-[11px] text-zinc-500">No stations yet</p>}
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1">Leave all unchecked to play on every station.</p>
                  <div className="mt-3">
                    <label className="text-[11px] font-bold text-zinc-400 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#E6B34A]" /> Days {days.length === 0 ? "(every day)" : ""}</label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="jingle-days">
                      {DAYS.map(([lbl, d]) => (
                        <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`jingle-day-${d}`}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors ${days.includes(d) ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#1E2A5A] text-zinc-300"}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Clock className="w-3.5 h-3.5 text-[#E6B34A] shrink-0" />
                    <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="jingle-start-time" className="rounded-lg bg-[#1E2A5A] border border-white/10 px-2 py-1 text-xs text-zinc-200" />
                    <span className="text-xs text-zinc-500">to</span>
                    <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="jingle-end-time" className="rounded-lg bg-[#1E2A5A] border border-white/10 px-2 py-1 text-xs text-zinc-200" />
                    <span className="text-[10px] text-zinc-500">blank = all day</span>
                  </div>
                </div>
              )}
              {opts.programs.length > 0 && (
                <div>
                  <label className="text-[11px] font-bold text-zinc-400">Attach to shows {programIds.length ? `(${programIds.length})` : ""}</label>
                  <div className="mt-1.5 max-h-24 overflow-y-auto rounded-lg border border-white/10 bg-[#0C1338] p-2 space-y-1">
                    {opts.programs.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                        <input type="checkbox" checked={programIds.includes(p.id)} onChange={() => toggleIn(programIds, setProgramIds, p.id)} className="accent-[#E6B34A]" data-testid={`jingle-program-${p.id}`} />
                        {p.title}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <button data-testid="jingle-save-btn" onClick={saveJingle} disabled={busy === "save"}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
                {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save jingle
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Library */}
      <div className={card}>
        <div className="flex items-center gap-2 mb-4">
          <Music className="w-5 h-5 text-[#E6B34A]" />
          <h3 className="text-lg font-bold text-white">Your jingles</h3>
          <span className="text-xs text-zinc-500">({jingles.length})</span>
        </div>
        {jingles.length === 0 ? (
          <p className="text-sm text-zinc-500 py-8 text-center">No jingles yet. Create one on the left — hosts can play them live and they'll ring out between songs on your stations.</p>
        ) : (
          <div className="space-y-2" data-testid="jingle-list">
            {jingles.map((j) => (
              <div key={j.id} className="rounded-lg border border-white/10 bg-[#1E2A5A] p-3" data-testid={`jingle-row-${j.id}`}>
                <div className="flex items-center gap-3">
                  <button onClick={() => playUrl(j.id, `${API}/jingles/${j.id}/audio`)} data-testid={`jingle-play-${j.id}`}
                    className="w-9 h-9 rounded-full bg-[#E6B34A] text-[#0A1030] flex items-center justify-center shrink-0">
                    {playingId === j.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate">{j.title}</p>
                    <p className="text-[11px] text-zinc-400 truncate">{j.length_sec}s · {j.plays} plays · {j.station_ids.length ? `${j.station_ids.length} station(s)` : "all stations"}</p>
                  </div>
                  <Sparkline daily={analytics[j.id]?.daily || []} week={analytics[j.id]?.week || 0} testid={`jingle-sparkline-${j.id}`} />
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggleFav(j)} className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center transition-colors ${j.favorite ? "text-[#E6B34A]" : "text-zinc-400 hover:text-[#E6B34A]"}`} title={j.favorite ? "Pinned to top of host list" : "Pin to top of host list"} data-testid={`jingle-fav-${j.id}`}><Star className="w-4 h-4" fill={j.favorite ? "currentColor" : "none"} /></button>
                    <a href={`${API}/jingles/${j.id}/audio?download=1`} className="w-8 h-8 rounded-lg bg-white/5 text-zinc-300 hover:bg-white/10 flex items-center justify-center" title="Download" data-testid={`jingle-download-${j.id}`}><Download className="w-4 h-4" /></a>
                    <button onClick={() => removeJingle(j.id)} className="w-8 h-8 rounded-lg bg-white/5 text-red-300 hover:bg-red-500/20 flex items-center justify-center" title="Delete" data-testid={`jingle-delete-${j.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-2.5 text-xs text-zinc-300 cursor-pointer">
                  <input type="checkbox" checked={j.in_rotation} onChange={() => toggleRotation(j)} className="accent-[#E6B34A] w-4 h-4" data-testid={`jingle-rotation-toggle-${j.id}`} />
                  <Radio className="w-3.5 h-3.5 text-[#E6B34A]" /> Auto-play between songs
                </label>
                {j.in_rotation && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    {schedEdit?.id === j.id ? (
                      <div className="space-y-2" data-testid={`jingle-sched-editor-${j.id}`}>
                        <div className="flex flex-wrap gap-1.5">
                          {DAYS.map(([lbl, d]) => (
                            <button key={d} type="button" onClick={() => toggleSchedDay(d)} data-testid={`sched-day-${j.id}-${d}`}
                              className={`px-2 py-0.5 rounded text-[10px] font-bold transition-colors ${schedEdit.days.includes(d) ? "bg-[#E6B34A] text-[#0A1030]" : "bg-[#0C1338] text-zinc-400"}`}>{lbl}</button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <input type="time" value={schedEdit.start_time} onChange={(e) => setSchedEdit((s) => ({ ...s, start_time: e.target.value }))} data-testid={`sched-start-${j.id}`} className="rounded bg-[#0C1338] border border-white/10 px-2 py-1 text-[11px] text-zinc-200" />
                          <span className="text-[11px] text-zinc-500">to</span>
                          <input type="time" value={schedEdit.end_time} onChange={(e) => setSchedEdit((s) => ({ ...s, end_time: e.target.value }))} data-testid={`sched-end-${j.id}`} className="rounded bg-[#0C1338] border border-white/10 px-2 py-1 text-[11px] text-zinc-200" />
                          <button onClick={saveSched} data-testid={`sched-save-${j.id}`} className="px-2.5 py-1 rounded bg-[#E6B34A] text-[#0A1030] text-[11px] font-bold">Save</button>
                          <button onClick={() => setSchedEdit(null)} className="px-2.5 py-1 rounded bg-white/5 text-zinc-400 text-[11px] font-bold">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => openSched(j)} data-testid={`jingle-sched-${j.id}`} className="flex items-center gap-1.5 text-[11px] text-zinc-400 hover:text-[#E6B34A] transition-colors">
                        <Clock className="w-3.5 h-3.5" /> {schedLabel(j)} <span className="underline ml-1">Edit</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
